<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon"><i class="pi pi-user-plus"></i></div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إضافة حجز {{ grade?.name ?? '' }}</h1>
        <p class="form-page-header__subtitle">تسجيل بيانات طالب جديد في الفصل الدراسي</p>
      </div>
    </div>

    <div class="container-fluid">
      <div v-if="loadingSettings" class="text-center py-5">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
      </div>

      <form v-else @submit.prevent="sendForm">

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-id-card"></i></div>
            <span class="form-card__title">البيانات الشخصية</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الطالب <span class="required">*</span></label>
                  <InputText v-model="student_name" placeholder="أدخل الاسم كاملاً" class="w-100" @input="v$.student_name.$touch()" />
                  <span class="field-error" v-if="v$.student_name.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.student_name.required.$invalid">برجاء ادخال اسم الطالب</template>
                    <template v-else-if="v$.student_name.arabicOnly.$invalid">يرجى إدخال اسم الطالب باللغة العربية فقط.</template>
                    <template v-else>يجب ألا يقل اسم الطالب عن 10 أحرف</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف الطالب <span class="required">*</span></label>
                  <InputText v-model="student_phone" inputmode="tel" placeholder="01XXXXXXXXX" dir="ltr" class="w-100" v-phone @input="v$.student_phone.$touch()" />
                  <span class="field-error" v-if="v$.student_phone.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.student_phone.required.$invalid">برجاء ادخال رقم هاتف الطالب</template>
                    <template v-else-if="v$.student_phone.exact11.$invalid">رقم الهاتف يجب أن يتكون من 11 رقم.</template>
                    <template v-else>يجب أن يبدأ الرقم بـ 01.</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف ولي الأمر <span class="optional">اختياري</span></label>
                  <InputText v-model="parent_phone" inputmode="tel" placeholder="01XXXXXXXXX" dir="ltr" class="w-100" v-phone @input="v$.parent_phone.$touch()" />
                  <span class="field-error" v-if="v$.parent_phone.$error">
                    <i class="pi pi-exclamation-circle"></i> رقم ولي الأمر يجب أن يتكون من 7 إلى 11 رقم.
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الجنس <span class="required">*</span></label>
                  <Dropdown v-model="selected_gender" :options="genders" showClear optionLabel="name" placeholder="اختر الجنس" class="w-100" />
                  <span class="field-error" v-if="v$.selected_gender.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار الجنس
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.education_type.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.education_type.label }}
                    <span class="required" v-if="formSettings.education_type.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <Dropdown
                    v-model="selected_type"
                    :options="educationOptions"
                    :disabled="!educationOptions.length"
                    showClear
                    optionLabel="name"
                    :placeholder="educationOptions.length ? 'اختر نظام التعليم' : 'لا توجد أنظمة تعليمية متاحة'"
                    class="w-100"
                    @change="v$.selected_type.$touch()"
                  />
                  <span class="field-error" v-if="v$.selected_type.$error">
                    <i class="pi pi-exclamation-circle"></i> {{ formSettings.education_type.error_required }}
                  </span>
                  <span class="field-hint" v-else-if="!educationOptions.length && grade">
                    لا توجد أنظمة تعليمية متاحة لهذه السنة الدراسية. برجاء إضافتها من إعدادات السنة الدراسية أولاً.
                  </span>
                </div>
              </div>

              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">وضع الدراسة <span class="required">*</span></label>
                  <div class="type-selector">
                    <label
                      v-for="option in studyModeOptions"
                      :key="option.value"
                      :class="['type-selector__item', { active: selected_study_mode?.value === option.value }]"
                    >
                      <input type="radio" v-model="selected_study_mode" :value="option" class="d-none" />
                      <i :class="`pi ${option.icon}`"></i>
                      {{ option.label }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.student_type.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.student_type.label }}
                    <span class="required" v-if="formSettings.student_type.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <Dropdown v-model="selected_student_type" :options="studentTypes" showClear optionLabel="name" placeholder="اختر نوع الطالب" class="w-100" />
                  <span class="field-error" v-if="v$.selected_student_type.$error">
                    <i class="pi pi-exclamation-circle"></i> {{ formSettings.student_type.error_required }}
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.whatsapp_number.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.whatsapp_number.label }}
                    <span class="required" v-if="formSettings.whatsapp_number.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText v-model="whatsapp_number" inputmode="tel" :placeholder="formSettings.whatsapp_number.placeholder" dir="ltr" class="w-100" v-phone @input="v$.whatsapp_number.$touch()" />
                  <span class="field-error" v-if="v$.whatsapp_number.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.whatsapp_number.required?.$invalid">{{ formSettings.whatsapp_number.error_required }}</template>
                    <template v-else-if="v$.whatsapp_number.exact11.$invalid">{{ formSettings.whatsapp_number.error_digits }}</template>
                    <template v-else>{{ formSettings.whatsapp_number.error_prefix }}</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.address.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.address.label }}
                    <span class="required" v-if="formSettings.address.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText v-model="address" :placeholder="formSettings.address.placeholder" class="w-100" />
                  <span class="field-error" v-if="v$.address.$error">
                    <i class="pi pi-exclamation-circle"></i> {{ formSettings.address.error_required }}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div class="form-card" v-if="formSettings.certificate_image.visible">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-file-import"></i></div>
            <span class="form-card__title">{{ formSettings.certificate_image.label }}</span>
          </div>
          <div class="form-card__body">
            <label class="field-label">
              رفع الشهادة
              <span class="required" v-if="formSettings.certificate_image.required">*</span>
              <span class="optional" v-else>اختياري</span>
            </label>
            <div :class="['upload-zone', { 'upload-zone--has-file': imageEntry }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${imageEntry ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ imageEntry ? fileData.name : formSettings.certificate_image.upload_text }}</p>
              <p class="upload-zone__hint" v-if="!imageEntry">{{ formSettings.certificate_image.upload_hint }}</p>
              <input type="file" @change="onFileChange" :accept="certificateAccept" />
            </div>
            <span class="field-error" v-if="v$.fileData.$error">
              <i class="pi pi-exclamation-circle"></i> {{ formSettings.certificate_image.error_required }}
            </span>
          </div>
        </div>

        <!-- <div class="notice-box notice-box--warning">
          <i class="pi pi-whatsapp" style="font-size:1.25rem;"></i>
          <div>
            <strong>هل واجهتك مشكلة في الحجز؟</strong>
            <p class="mb-0 mt-1" style="font-size:0.875rem;">
              تواصل معنا على الواتساب:
              <a href="https://wa.me/+201111488849" target="_blank" style="font-weight:700; margin-inline:4px;">01111488849</a>
            </p>
          </div>
        </div> -->

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الإرسال...' : 'تأكيد الحجز' }}
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InputText from 'primevue/inputtext';
import Swal from 'sweetalert2';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Dropdown from 'primevue/dropdown';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';
import { scrollToFirstError } from '@/composables/useScrollToFirstError';
import { CERTIFICATE_ACCEPT, certificateUploadError, revokeCertificatePreview, validateCertificateImage } from '@/services/certificateUpload';
import {
  DEFAULT_FORM_SETTINGS,
  arabicOnly,
  digitsBetween,
  exactDigits,
  startsWithZeroOne,
  toEnglishDigits,
  trimmedMinLength,
} from '@/composables/useRegistrationValidators';

const STUDY_MODE_OPTIONS = [
  { label: 'أوفلاين فقط', value: 'offline', icon: 'pi-building' },
  { label: 'أونلاين فقط', value: 'online', icon: 'pi-wifi' },
  { label: 'أوفلاين + أونلاين', value: 'hybrid', icon: 'pi-sync' },
];

// Default study mode for a new Dashboard registration — the combined mode
// ('hybrid', the project's existing value for "أونلاين + حضوري"), not the first
// option in the list.
const DEFAULT_STUDY_MODE = STUDY_MODE_OPTIONS.find((o) => o.value === 'hybrid');

export default {
  name: 'AddGradeStudent',
  components: { InputText, Dropdown, Breadcrumb },

  data() {
    return {
      v$: useVuelidate(),
      student_name: '',
      student_phone: null,
      parent_phone: null,
      address: '',
      whatsapp_number: null,
      imageEntry: '',
      fileData: '',
      certificateAccept: CERTIFICATE_ACCEPT,
      formSending: false,
      loadingSettings: true,
      formSettings: DEFAULT_FORM_SETTINGS,
      selected_gender: null,
      selected_type: null,
      selected_student_type: null,
      selected_study_mode: DEFAULT_STUDY_MODE,
      studyModeOptions: STUDY_MODE_OPTIONS,
      genders: [
        { name: 'ولد', id: 1 },
        { name: 'بنت', id: 2 },
      ],
      studentTypes: [
        { name: 'مستجد', id: 1 },
        { name: 'قديم', id: 2 },
      ],
    };
  },

  validations() {
    const fs = this.formSettings;
    return {
      student_name: { required, arabicOnly, minLength10: trimmedMinLength(10) },
      student_phone: { required, exact11: exactDigits(11), startsWith01: startsWithZeroOne },
      parent_phone: { digitsBetween: digitsBetween(7, 11) },
      selected_gender: { required },
      address: fs.address.required ? { required } : {},
      whatsapp_number: {
        ...(fs.whatsapp_number.required ? { required } : {}),
        exact11: exactDigits(fs.whatsapp_number.validation?.length ?? 11),
        startsWith01: startsWithZeroOne,
      },
      // education_type must belong to the selected Academic Year (educationOptions,
      // sourced from formSettings.education_type.options) — enforced by only ever
      // offering those options in the dropdown, plus the backend's own
      // Rule::in($availableEducationSystemIds) check on submit.
      selected_type: fs.education_type?.required ? { required } : {},
      selected_student_type: fs.student_type.required ? { required } : {},
      fileData: fs.certificate_image.required ? { required } : {},
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },
    gradeSlug() { return this.$route.params.gradeSlug; },
    grade() {
      return this.gradeStore.activeGrades.find(g => g.slug === this.gradeSlug)
          || this.gradeStore.allGrades.find(g => g.slug === this.gradeSlug)
          || null;
    },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.grade?.name ?? 'المرحلة', path: `/grades/${this.gradeSlug}/students` },
        { name: 'إضافة حجز', path: `/grades/${this.gradeSlug}/students/add` },
      ];
    },
    // Education systems configured for the CURRENTLY selected Academic Year only —
    // sourced from formSettings.education_type.options (the same
    // RegistrationFormSettings data the "الأنظمة التعليمية المتاحة" admin settings
    // screen and the public reservation form already use). Only options marked
    // available for this year are offered, so an option can never be submitted for
    // a year it doesn't belong to / isn't currently enabled for.
    educationOptions() {
      return (this.formSettings.education_type?.options || []).filter((o) => o.available !== false);
    },
  },

  methods: {
    // Reset + reload whenever the selected Academic Year changes. The old
    // education_type selection is cleared immediately (never left silently selected
    // for a year it doesn't belong to) and only replaced once the new year's real
    // options are known.
    async onAcademicYearChanged() {
      this.selected_type = null;
      this.loadingSettings = true;
      await this.fetchFormSettings();
      this.autoSelectSoleEducationOption();
      this.loadingSettings = false;
    },

    // "If there is only one configured education type, select it automatically."
    autoSelectSoleEducationOption() {
      if (this.educationOptions.length === 1) {
        this.selected_type = this.educationOptions[0];
      }
    },

    async onFileChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      const result = await validateCertificateImage(file);
      if (!result.valid) {
        this.clearCertificateFile();
        e.target.value = '';
        Swal.fire({ position: 'center', icon: 'error', title: result.message });
        return;
      }
      revokeCertificatePreview(this.imageEntry);
      this.fileData = file;
      this.imageEntry = URL.createObjectURL(file);
    },

    clearCertificateFile() {
      revokeCertificatePreview(this.imageEntry);
      this.imageEntry = '';
      this.fileData = '';
    },

    onlyNumbers(e) {
      const char = String.fromCharCode(e.which);
      if (!/[0-9٠-٩]/.test(char)) { e.preventDefault(); return; }
      const digits = (e.target.value.match(/[0-9٠-٩]/g) || []).length;
      if (digits >= 11) e.preventDefault();
    },

    toEnglishDigits(str) {
      return toEnglishDigits(str);
    },

    async fetchFormSettings() {
      if (!this.grade?.id) {
        this.formSettings = DEFAULT_FORM_SETTINGS;
        return;
      }
      try {
        const { data } = await axios.get(`/academic-years/${this.grade.id}/registration-settings`);
        this.formSettings = data.form_settings;
      } catch {
        this.formSettings = DEFAULT_FORM_SETTINGS;
      }
    },

    async sendForm() {
      // Defensive: never submit an education_type that isn't (still) one of the
      // selected Academic Year's configured options — e.g. if it became invalid
      // between selection and submit (year settings changed underneath the form).
      if (this.selected_type && !this.educationOptions.some((o) => o.id === this.selected_type.id)) {
        this.selected_type = null;
      }

      this.v$.$validate();
      if (this.v$.$error) {
        this.$nextTick(() => scrollToFirstError());
        return;
      }

      const fd = new FormData();
      fd.append('student_name', this.student_name);
      fd.append('student_phone', this.toEnglishDigits(this.student_phone));
      if (this.parent_phone) fd.append('parent_phone', this.toEnglishDigits(this.parent_phone));
      fd.append('gender', this.selected_gender.id);
      if (this.selected_type) fd.append('education_type', this.selected_type.id);
      fd.append('study_mode', this.selected_study_mode?.value || 'hybrid');

      if (this.grade?.id) fd.append('academic_year_id', this.grade.id);

      if (this.formSettings.address.visible && this.address) fd.append('address', this.address);
      if (this.formSettings.whatsapp_number.visible && this.whatsapp_number) {
        fd.append('whatsapp_number', this.toEnglishDigits(this.whatsapp_number));
      }
      // student_actual_grade / maximum_grade are intentionally NOT collected or sent
      // by Dashboard registration — that's a public/web-registration-only concept
      // (see web/src/views/reservation.vue). Do not reintroduce them here.
      if (this.formSettings.student_type.visible && this.selected_student_type) {
        fd.append('student_type', this.selected_student_type.id);
      }
      if (this.formSettings.certificate_image.visible && this.fileData instanceof File) {
        fd.append('certificate_image', this.fileData);
      }

      this.formSending = true;
      try {
        const response = await axios.post('/register_dashboard', fd);
        this.resetForm();
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'رقم الحجز: ' + response.data.data.reservation_number,
          // after_submit_message is trusted admin-authored HTML (Quill content from the
          // dashboard) — render it as HTML so tags like <h2>/<strong> and &nbsp; format
          // correctly instead of showing as raw text. Not user-supplied.
          html: response.data.data.after_submit_message,
        });
      } catch (err) {
        Swal.fire({ position: 'center', icon: 'error', title: certificateUploadError(err) });
      } finally {
        this.formSending = false;
      }
    },

    resetForm() {
      this.v$.$reset();
      this.student_name = '';
      this.student_phone = null;
      this.parent_phone = null;
      this.address = '';
      this.whatsapp_number = null;
      this.clearCertificateFile();
      this.selected_gender = null;
      this.selected_type = null;
      this.selected_student_type = null;
      this.selected_study_mode = DEFAULT_STUDY_MODE;
      this.autoSelectSoleEducationOption();
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    this.loadingSettings = true;
    await this.fetchFormSettings();
    this.autoSelectSoleEducationOption();
    this.loadingSettings = false;

    // Only start watching for Academic Year changes AFTER the initial load above
    // has settled — avoids a redundant duplicate fetch racing with the store
    // population while gradeStore was still loading. Vue Router reuses this
    // component instance across /grades/:gradeSlug/students/add navigations (same
    // route record, different param), so `grade` must be watched explicitly:
    // without this, an admin switching years within the same session would keep a
    // stale education_type selected that may not even belong to the new year.
    this.$watch(() => this.grade?.id, (newId, oldId) => {
      if (newId === oldId) return;
      this.onAcademicYearChanged();
    });
  },
  beforeUnmount() {
    revokeCertificatePreview(this.imageEntry);
  },
};
</script>
